import type {
  ThemePageQueryResponse,
  ThemePageQueryVariables,
} from '~/api/graphql/queries/themePage.generated';
import type { ThemePageEdgeSelectFragment } from '~/api/graphql/fragments/themePage.generated';
import type { useThemePageV6Query } from '~/api/gempages/queries/useThemePageV6Query';
import type { ThemePagesV6QueryResponse } from '~/api/gempages/types/page';
import type { IThemePage } from '~/stores/themePage';

import { convertGPV6ToTemplateType, convertGPV7ToTemplateType } from './templateConverters';
import { ThemePageDocument } from '~/api/graphql/queries/themePage.generated';
import { ProviderSource, TemplateType } from '~/types/template';
import { PROVIDER_CONFIG } from '~/constants/providerConfig';
import { IThemePageStatus } from '~/types/themePage';
import { appFetcher } from '~/api/utils';

type FetchThemePagesV6 = ReturnType<typeof useThemePageV6Query>['fetchManual'];

export const mapThemePagesV7 = (themePages?: ThemePageEdgeSelectFragment[]): IThemePage[] => {
  return (
    themePages?.reduce<IThemePage[]>((acc, page) => {
      const pageNode = page?.node;
      if (!pageNode) return acc;

      const dataThemePage = mapThemePageV7(pageNode);
      if (!dataThemePage) return acc;

      acc.push(dataThemePage);
      return acc;
    }, []) || []
  );
};

export const mapThemePageV7 = (
  themePage: ThemePageEdgeSelectFragment['node'],
): IThemePage | undefined => {
  if (!themePage) return;

  const thumbnail = themePage.metafields?.find(
    (metafield) => metafield?.key === 'capture_page_thumbnail',
  );

  const thumbnailDefault = PROVIDER_CONFIG[ProviderSource.GEMPAGESV7].thumbnail;

  return {
    id: themePage.id,
    thumbnail: thumbnail?.value ?? thumbnailDefault,
    name: themePage.name,
    type: convertGPV7ToTemplateType(themePage.type) || TemplateType.PAGE,
    status: (themePage.status as IThemePageStatus) ?? IThemePageStatus.DRAFT,
    default: themePage.default && !!themePage.publishedAt,
    publishedAt: themePage.publishedAt ?? null,
    updatedAt: themePage.updatedAt ?? '',
    parent: themePage.parent,
    providerSource: ProviderSource.GEMPAGESV7,
  };
};

export const isThemePageDraft = (
  status?: IThemePageStatus,
  publishedAt?: string | null,
): boolean => {
  if (status === IThemePageStatus.DRAFT) return true;
  if (status === IThemePageStatus.PUBLISH_ERROR) return !publishedAt;
  return false;
};

export const hasThemePageID = (themePageID?: string) => {
  return !!themePageID && themePageID !== '0';
};

export const mapThemePagesV6 = (themePages?: ThemePagesV6QueryResponse): IThemePage[] => {
  return (
    themePages?.data?.reduce<IThemePage[]>((acc, page) => {
      if (!page) return acc;

      const thumbnailDefault = PROVIDER_CONFIG[ProviderSource.GEMPAGESV6].thumbnail;

      acc.push({
        id: `${page.id}`,
        thumbnail: page.thumbnail ?? thumbnailDefault,
        name: page.name,
        type: convertGPV6ToTemplateType(page.type) || TemplateType.PAGE,
        status: page.status ?? '',
        default: page.default,
        updatedAt: page.updatedAt ?? '',
        providerSource: ProviderSource.GEMPAGESV6,
      });

      return acc;
    }, []) || []
  );
};

export const fetchThemePageV7ById = async (id: string): Promise<IThemePage | undefined> => {
  const response = await appFetcher<ThemePageQueryResponse, ThemePageQueryVariables>(
    ThemePageDocument,
    { id },
  )();
  return mapThemePageV7(response?.themePage);
};

export const fetchThemePageV6ById = async (
  id: string,
  fetchThemePagesV6: FetchThemePagesV6,
): Promise<IThemePage | undefined> => {
  const { data } = await fetchThemePagesV6({ where: { idIn: [id] } });
  return mapThemePagesV6(data)[0];
};

export const fetchThemePageById = async (
  templateId: string | undefined,
  isV7: boolean,
  fetchThemePagesV6: FetchThemePagesV6,
): Promise<IThemePage | undefined> => {
  if (!templateId) return undefined;
  return isV7
    ? fetchThemePageV7ById(templateId)
    : fetchThemePageV6ById(templateId, fetchThemePagesV6);
};
